import React from "react";
import { motion } from "framer-motion";
import { MANIFESTO } from "@/lib/site";
import { fadeUp, EASE_OUT_QUART } from "@/lib/motion";

/**
 * Manifesto
 * Numbered chapters. Editorial layout, two-column alternating.
 * Real dealership vehicle photography, framed as horizontal editorial
 * plates. Deliberately no created UI mockups here — the manifesto
 * argument reads more clearly with actual inventory in-shot.
 */
const CHAPTER_IMAGES = [
  { src: "/generated/hero/ford-f150-xlt.png", position: "center 62%" },
  { src: "/generated/hero/toyota-rav4-xle.png", position: "center 62%" },
  { src: "/generated/hero/chevrolet-silverado-lt.png", position: "center 62%" },
  { src: "/generated/black-suv-after.png", position: "center 62%" },
];

export default function Manifesto() {
  return (
    <section
      data-testid="manifesto-section"
      className="relative bg-white text-black"
    >
      <div className="container-lw section-lw">
        {/* Section index */}
        <div className="mb-14 flex items-baseline justify-between border-b border-black/10 pb-6 md:mb-20">
          <div className="eyebrow text-[#4F0DFF]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#4F0DFF]" />
            The LotWise Manifesto
          </div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.28em] text-black/40">
            Chapters 01 to 04
          </div>
        </div>

        <div className="space-y-24 md:space-y-32">
          {MANIFESTO.map((c, i) => {
            const isEven = i % 2 === 0;
            return (
              <article
                key={c.n}
                data-testid={`manifesto-chapter-${c.n}`}
                className={`grid gap-10 md:gap-16 lg:grid-cols-[1fr_1fr] ${
                  isEven ? "" : "lg:grid-flow-col-dense"
                }`}
              >
                {/* Copy column */}
                <motion.div
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, margin: "-100px" }}
                  variants={fadeUp}
                  className={isEven ? "" : "lg:col-start-2"}
                >
                  <div className="flex items-baseline gap-6">
                    <span className="font-display text-6xl font-bold leading-none tracking-tight text-black/10 md:text-8xl">
                      {c.n}
                    </span>
                    <div className="h-px flex-1 bg-black/10" />
                    <span className="text-[10px] font-semibold uppercase tracking-[0.28em] text-black/50">
                      {c.kicker}
                    </span>
                  </div>
                  <h3 className="mt-6 h-display text-3xl md:text-4xl lg:text-5xl">
                    {c.title}
                  </h3>
                  <p className="mt-6 max-w-xl text-[17px] leading-relaxed text-[#4A5359]">
                    {c.body}
                  </p>
                </motion.div>

                {/* Image plate — clipped and spotlighted */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.97 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 1, ease: EASE_OUT_QUART }}
                  className={`relative ${
                    isEven ? "lg:col-start-2" : "lg:col-start-1 lg:row-start-1"
                  }`}
                >
                  <div className="relative aspect-[4/3] w-full overflow-hidden rounded-[4px] border border-black/10 bg-black">
                    <img
                      src={CHAPTER_IMAGES[i % CHAPTER_IMAGES.length].src}
                      alt={`Chapter ${c.n} — ${c.title}`}
                      className="absolute inset-0 h-full w-full object-cover"
                      style={{
                        objectPosition:
                          CHAPTER_IMAGES[i % CHAPTER_IMAGES.length].position,
                      }}
                      loading="lazy"
                      decoding="async"
                    />
                    <div
                      aria-hidden
                      className="pointer-events-none absolute inset-0"
                      style={{
                        background:
                          "linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0) 40%, rgba(0,0,0,0.5) 100%)",
                      }}
                    />
                    <div className="absolute left-4 top-4 inline-flex items-center gap-2 rounded-full bg-black/70 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.24em] text-white backdrop-blur-sm">
                      <span className="h-px w-4 bg-[#0AB5FF]" />
                      Plate 0{i + 2}
                    </div>
                    <div className="absolute bottom-4 right-4 rounded-full bg-black/70 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.24em] text-white/85 backdrop-blur-sm">
                      LWA — {String(i + 2).padStart(4, "0")}
                    </div>
                  </div>
                </motion.div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
